import { locationsAdapter } from '@/adapters/locations';
import { searchBannersAdapter } from '@/adapters/search-banners';
import {
  routesAdapter,
  routeAdapter,
  routeDatePreviewsAdapter,
  routeAlertsAdapter,
} from '@/adapters/routes';
import { WS } from '@/services/centrifuge';
import { getDefaultSearchDate } from '@/utils/helpers';

export function getInitialFilters(setDepartureDate = true) {
  const defaultDate = getDefaultSearchDate();

  return {
    // dataUpdatedAt: 0,

    // station
    departureStation: null,
    arrivalStation: null,

    // date
    roundTrip: false,
    departureDate: setDepartureDate ? defaultDate : null,
    returnDate: null,

    // time
    departureTimeType: null,
    departureTime: null,

    returnTimeType: null,
    returnTime: null,

    // seats
    adults: 1,
    children: 0,
    infants: 0,
    seatsForInfants: false,

    // cartDataName: 0,
  };
}

export const useSearchStore = defineStore({
  id: 'search',
  state: () => ({
    filters: cloneObject(getInitialFilters(false)),
    initialFilters: cloneObject(getInitialFilters(false)),
    filtersReset: false,

    searchLocations: {},
  }),
  getters: {
    isFiltersInitial() {
      // TODO: exclude? roundTrip
      return (
        JSON.stringify(this.filters) ===
        JSON.stringify(getInitialFilters(false))
      );
    },
  },
  actions: {
    updateInitialFilters(filters) {
      this.initialFilters = filters;
    },
    updateFilters(filters = {}) {
      Object.assign(this.filters, filters);
    },
    resetFilters() {
      this.filters = cloneObject(getInitialFilters(false));
      this.filtersReset = true;
    },

    setSearchLocations(locations) {
      this.searchLocations = locations;
    },

    // pure api requests
    fetchLocations({ filter = {}, sort = {} } = {}) {
      return useRequest(
        '/locations',
        {
          query: {
            filter: {
              id: filter.id,
              opposite_location_id: filter.oppositeLocationId,
              name: filter.name,
              is_without_stations: filter.isWithoutStations,
            },
            _sort: {
              field: sort.field,
              type: sort.type,
            },
          },
        },
        'v2',
      ).then(({ cities }) => locationsAdapter(cities));
    },

    fetchRoutes(
      { filter = {}, sort = {}, limit } = {},
      apiVersion = 'v1',
      wsCallback,
    ) {
      WS.hasSubscription() && WS.disconnect();

      return useRequest(
        '/routes',
        {
          query: {
            filter: {
              departure_location_id: filter.departureLocationId,
              arrival_location_id: filter.arrivalLocationId,
              departs_date: filter.departsDate,
              time_type: filter.timeType,
              time: filter.time,
              disable_log: filter.disableLog,
              adult_count: filter.adultCount,
              child_list: filter.childList,
            },
            _sort: {
              field: sort.field,
              type: sort.type,
            },
            limit,
          },
        },
        apiVersion,
      ).then((data) => {
        const { routes, centrifugo_channel, route_alerts, search_log_id } =
          data;

        const result = {
          routes: routesAdapter(routes),
          routeAlerts: routeAlertsAdapter(route_alerts),
          searchLogId: search_log_id,
        };

        if (centrifugo_channel && wsCallback) {
          WS.hasSubscription() && WS.disconnect();

          WS.connect(
            centrifugo_channel,
            {
              resolve: ({ data: { unsubscribe, routes } }) => {
                if (unsubscribe) return WS.disconnect();

                return wsCallback({
                  routes: routesAdapter(routes),
                  routeAlerts: result.routeAlerts,
                  searchLogId: search_log_id,
                });
              },
              reject: () => {
                return wsCallback(result);
              },
            },
            'search',
          );
        }

        return result;
      });
    },
    fetchSeoRoutes({ filter = {}, sort = {}, limit } = {}) {
      return useRequest('/routes', {
        query: {
          filter: {
            departure_location_id: filter.departureLocationId,
            arrival_location_id: filter.arrivalLocationId,
            departs_date: filter.departsDate,
            time_type: filter.timeType,
            time: filter.time,
            disable_log: filter.disableLog,
            adult_count: filter.adultCount,
            child_list: filter.childList,
          },
          _sort: {
            field: sort.field,
            type: sort.type,
          },
          limit,
        },
      }).then(({ routes }) => routesAdapter(routes));
    },
    fetchRoute(routeId, filter = {}, apiVersion = 'v1') {
      return useRequest(
        `/routes/${routeId}`,
        {
          query: {
            filter: {
              adult_count: filter.adultCount,
              child_list: filter.childList,
            },
          },
        },
        apiVersion,
      ).then((route) => routeAdapter(route));
    },

    fetchRouteDatePreviews({ filter = {} } = {}) {
      return useRequest('/routes/date-previews', {
        query: {
          departure_location_id: filter.departureLocationId,
          arrival_location_id: filter.arrivalLocationId,
          date_from: filter.dateFrom,
          date_to: filter.dateTo,
        },
      }).then(({ data }) => routeDatePreviewsAdapter(data));
    },

    fetchBanners({ filter = {} } = {}) {
      return useRequest('/routes/banners', {
        query: {
          filter: {
            departure_location_id: filter.departureLocationId,
            arrival_location_id: filter.arrivalLocationId,
            date: filter.date,
          },
        },
      }).then(({ data }) => searchBannersAdapter(data));
    },
  },
});
